import React, { useState, useEffect, Suspense } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { AnimatePresence, useCycle } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

//hooks
import useAxios from "../../hooks/useAxios";
import useSortContent from "../../hooks/useSortContent";

//comps
const PageGrid = React.lazy(() => import("../PageGrid"));
const ContentGrid = React.lazy(() => import("../ContentGrid"));
const Filters = React.lazy(() => import("../Filters"));
const FeaturedFoodCard = React.lazy(() => import("./FeaturedFoodCard"));
const FoodVendorCard = React.lazy(() => import("./FoodVendorCard"));
const SidePanel = React.lazy(() => import("../SidePanel"));
const NoResults = React.lazy(() => import("../NoResults"));
import Loading from "../Loading";

const App = ({ filtersJson, filtersTaxonomy }) => {
	const getInitialUrl = (endPoint) => {
		let url = endPoint;
		if (filters && filters.length) {
			url = `${endPoint}?${filtersTaxonomy}=`;
			filters.forEach((filter) => {
				if (filter.slug !== "all") {
					url = url.concat(`${filter.slug},`);
				}
			});
			url = url.slice(0, -1);
		}
		return url;
	};

	const [filters] = useState(filtersJson ? JSON.parse(filtersJson) : []);
	const [selectedFilter, setSelectedFilter] = useState({
		slug: "all",
	});
	const [selectedFood, setSelectedFood] = useState({});
	const [open, cycleOpen] = useCycle(false, true);
	const [endPoint] = useState(getInitialUrl("food"));
	const [selectedSortOption, setSelectedSortOption] =
		useState("alphabetically");

	const food = useAxios(endPoint);
	const isFood = true;
	const sortedFood = useSortContent(food, selectedSortOption);

	useEffect(() => {
		// add parallax effect to the curved header
		gsap.registerPlugin(ScrollTrigger);
		gsap.to(".performance-index__header", {
			yPercent: -50,
			ease: "none",
			duration: 2000,
			scrollTrigger: {
				trigger: ".performance-index__header",
				start: "top 100px",
				scrub: true,
			},
		});
	}, []);

	// useEffect responsible for displaying the sidepanel from a sharable url
	useEffect(() => {
		if (food.length > 0 && window.location.hash) {
			// we extract the performer from the Hash
			let trimmedIncomingFoodItem = window.location.hash.replace("#", "");
			// find the food we want from the food array using the trimmed hash
			let selectedFood = food.find(
				(foodItem) => foodItem.slug == trimmedIncomingFoodItem
			);
			// we open the sidePanel
			if (selectedFood) {
				toggleSidePanel(selectedFood);
			}
		}
	}, [food]);

	// handles the scroll behaviour on open and closing the sidePanel
	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "scroll";
		}
	}, [open]);

	const selectFilter = (filter) => {
		setSelectedFilter(filter);
	};

	// responsible for opening and closing the sidepanel
	// it lives in the App because it gets passed down to multiple components
	const toggleSidePanel = (foodItem) => {
		if (foodItem) {
			cycleOpen(true);
			setSelectedFood(foodItem);
			updateUrl(foodItem);
		} else {
			cycleOpen(false);
			setSelectedFood({});
			window.history.pushState({}, "", window.location.pathname);
		}
	};

	const updateUrl = (foodItem) => {
		let urlParams = "";
		if (foodItem) {
			// we use the hash param in window.location to store the foodItem slug
			urlParams = "#" + foodItem.slug;
		}
		window.history.pushState(null, "", urlParams);
	};

	const filterFood = () => {
		return sortedFood.filter(
			(foodItem) => foodItem["filter-slug"] === selectedFilter.slug
		);
	};

	const renderFoodCards = () => {
		if (sortedFood.length == 0) {
			return <NoResults text={"Stay tuned for upcoming food"} />;
		} else {
			if (selectedFilter.slug === "all") {
				return sortedFood.map((foodItem) => {
					if (foodItem.isVendor) {
						return (
							<FoodVendorCard
								foodItem={foodItem}
								key={foodItem.id}
								toggleSidePanel={toggleSidePanel}
								isSidePanelOff={true} // temp solution for not using sidepanels
							/>
						);
					}
					return (
						<FeaturedFoodCard
							foodItem={foodItem}
							key={foodItem.id}
							toggleSidePanel={toggleSidePanel}
							isSidePanelOff={true} // temp solution for not using sidepanels
						/>
					);
				});
			} else {
				const filteredFood = filterFood();
				if (filteredFood.length) {
					return filteredFood.map((foodItem) => {
						return (
							<FoodVendorCard
								foodItem={foodItem}
								key={foodItem.id}
								toggleSidePanel={toggleSidePanel}
								isSidePanelOff={true} // temp solution for not using sidepanels
							/>
						);
					});
				} else {
					return <NoResults text={"Select another filter to view food"} />;
				}
			}
		}
	};

	const selectSortOption = (sortOption) => {
		setSelectedSortOption(sortOption);
	};
	return (
		<section aria-label="Food Index">
			<Suspense fallback={<Loading />}>
				<PageGrid>
					<CenteredContent>
						{filters && filters.length > 2 && (
							<Filters
								filters={filters}
								selectedFilter={selectedFilter}
								selectFilter={selectFilter}
								selectSortOption={selectSortOption}
								selectedSortOption={selectedSortOption}
								isFood={isFood}
							/>
						)}
					</CenteredContent>
					<ContentGrid>{renderFoodCards()}</ContentGrid>
				</PageGrid>
				<AnimatePresence>
					{/* SidePanel is currently disabled for rides */}
					{open && selectedFood && (
						<SidePanel
							selectedContent={selectedFood}
							toggleSidePanel={toggleSidePanel}
						/>
					)}
				</AnimatePresence>
			</Suspense>
		</section>
	);
};

export default App;

App.propTypes = {
	pageTitle: PropTypes.string,
	filtersJson: PropTypes.any,
	filtersTaxonomy: PropTypes.string,
};

const CenteredContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;
